import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Send, ArrowLeft, Shield, AlertTriangle, Check, Phone, Mail, Clock, Lock } from 'lucide-react';
import useTheme from '../hooks/useTheme';

const API = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Messaging status
  const [messagingStatus, setMessagingStatus] = useState({
    can_message: false,
    messages_remaining: null,
    new_conversations_remaining: null,
    is_id_verified: false,
    requires_email_verification: false,
    requires_phone_verification: false,
    is_suspended: false,
    reset_time: null
  });

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('nearU_auth_token');
      const response = await axios.get(`${API}/api/messaging/conversations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setConversations(response.data.conversations || []);
      setMessagingStatus(response.data.messaging_status || {});
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      // Use dummy data if API fails
      setConversations([
        { id: 'c1', name: 'Camila', photo: 'https://i.pravatar.cc/150?img=48', lastMessage: 'See you at LIV tonight!', timestamp: '2025-01-15T20:30:00Z', unread: 2 },
        { id: 'c2', name: 'Eric', photo: 'https://i.pravatar.cc/150?img=15', lastMessage: 'That DJ set was amazing', timestamp: '2025-01-15T19:15:00Z', unread: 0 },
        { id: 'c3', name: 'Rafael', photo: 'https://i.pravatar.cc/150?img=14', lastMessage: 'Want to join our table?', timestamp: '2025-01-15T18:45:00Z', unread: 1 },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    if (!messagingStatus.can_message) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('nearU_auth_token');
      const response = await axios.post(`${API}/api/messaging/send`, {
        conversation_id: selectedConversation.id,
        recipient_id: selectedConversation.recipientId || 'unknown',
        message: newMessage,
        is_new_conversation: messages.length === 0
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Update local state
      setMessages([...messages, {
        id: response.data.message_id,
        text: newMessage,
        timestamp: new Date().toISOString(),
        isOwn: true
      }]);
      setNewMessage('');
      
      // Update messaging status
      if (response.data.messages_remaining !== undefined) {
        setMessagingStatus(prev => ({
          ...prev,
          messages_remaining: response.data.messages_remaining,
          new_conversations_remaining: response.data.new_conversations_remaining
        }));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send message';
      alert(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const colors = {
    bg: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.3)' : 'rgba(139, 105, 20, 0.2)',
    gold: isDark ? '#D4AF37' : '#8B6914',
    accent: isDark ? '#A57C33' : '#6B5210'
  };

  // Verification Required Banner
  const VerificationBanner = () => {
    if (messagingStatus.is_id_verified) return null;
    
    if (messagingStatus.requires_email_verification || messagingStatus.requires_phone_verification) {
      return (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          margin: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <Lock size={24} color="#EF4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ color: '#EF4444', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
              Verification Required
            </div>
            <p style={{ color: colors.textMuted, fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
              Please verify your email and phone number to start messaging other members.
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
              {messagingStatus.requires_email_verification && (
                <button
                  onClick={() => navigate('/verify')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#EF4444',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Mail size={14} /> Verify Email
                </button>
              )}
              {messagingStatus.requires_phone_verification && (
                <button
                  onClick={() => navigate('/verify')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#EF4444',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Phone size={14} /> Verify Phone
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (messagingStatus.is_suspended) {
      return (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          margin: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertTriangle size={24} color="#EF4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ color: '#EF4444', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
              Messaging Suspended
            </div>
            <p style={{ color: colors.textMuted, fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
              Your messaging has been suspended due to multiple reports. Please verify your identity to restore messaging privileges.
            </p>
            <button
              onClick={() => navigate('/verify')}
              style={{
                marginTop: '12px',
                padding: '10px 20px',
                background: colors.gold,
                border: 'none',
                borderRadius: '8px',
                color: isDark ? '#1A0F0A' : '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Verify Identity
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Message Limit Banner (for unverified users)
  const MessageLimitBanner = () => {
    if (messagingStatus.is_id_verified) return null;
    if (!messagingStatus.can_message) return null;
    if (messagingStatus.messages_remaining === null) return null;
    
    const isLow = messagingStatus.messages_remaining <= 3;
    
    return (
      <div 
        data-testid="message-limit-banner"
        style={{
          background: isLow 
            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(107, 70, 193, 0.1) 0%, rgba(107, 70, 193, 0.05) 100%)',
          border: `1px solid ${isLow ? 'rgba(245, 158, 11, 0.3)' : 'rgba(107, 70, 193, 0.25)'}`,
          borderRadius: '12px',
          padding: '12px 16px',
          margin: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Clock size={18} color={isLow ? '#F59E0B' : '#6B46C1'} />
          <div>
            <span style={{ color: colors.text, fontSize: '14px' }}>
              <strong>{messagingStatus.messages_remaining}</strong> messages remaining today
            </span>
            {messagingStatus.new_conversations_remaining !== null && (
              <span style={{ color: colors.textMuted, fontSize: '13px', marginLeft: '12px' }}>
                ({messagingStatus.new_conversations_remaining} new chats)
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate('/verify')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            background: 'transparent',
            border: `1px solid ${colors.gold}`,
            borderRadius: '8px',
            color: colors.gold,
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Shield size={14} /> Verify for Unlimited
        </button>
      </div>
    );
  };

  // ID Verified Badge
  const VerifiedBadge = () => {
    if (!messagingStatus.is_id_verified) return null;
    
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        background: 'linear-gradient(135deg, rgba(72, 187, 120, 0.15) 0%, rgba(72, 187, 120, 0.05) 100%)',
        border: '1px solid rgba(72, 187, 120, 0.3)',
        borderRadius: '20px',
        margin: '16px'
      }}>
        <Check size={14} color="#48BB78" />
        <span style={{ color: '#48BB78', fontSize: '12px', fontWeight: '600' }}>ID Verified</span>
        <span style={{ color: colors.textMuted, fontSize: '12px' }}>Unlimited messaging</span>
      </div>
    );
  };

  // Limit Reached Message
  const LimitReachedMessage = () => {
    if (messagingStatus.can_message) return null;
    if (messagingStatus.messages_remaining !== 0) return null;
    
    return (
      <div 
        data-testid="limit-reached-message"
        style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          margin: '16px',
          textAlign: 'center'
        }}
      >
        <AlertTriangle size={32} color="#F59E0B" style={{ marginBottom: '12px' }} />
        <p style={{ color: colors.text, fontSize: '15px', margin: 0, lineHeight: '1.6' }}>
          You've reached today's messaging limit. Verify your identity to continue messaging now, or your limit will reset in 24 hours.
        </p>
        <button
          onClick={() => navigate('/verify')}
          style={{
            marginTop: '16px',
            padding: '12px 24px',
            background: colors.gold,
            border: 'none',
            borderRadius: '10px',
            color: isDark ? '#1A0F0A' : '#FFFFFF',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Verify Identity Now
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: colors.textMuted }}>Loading messages...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bg,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          color: colors.gold,
          margin: 0
        }}>
          Messages
        </h1>
        <VerifiedBadge />
      </div>

      {/* Verification/Limit Banners */}
      <VerificationBanner />
      <MessageLimitBanner />
      <LimitReachedMessage />

      {/* Conversation List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Search */}
        <div style={{ padding: '16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: isDark ? 'rgba(26, 15, 10, 0.8)' : 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${colors.border}`,
            borderRadius: '12px'
          }}>
            <Search size={18} color={colors.textMuted} />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: colors.text,
                fontSize: '15px',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Conversations */}
        {conversations.filter(c => 
          c.name.toLowerCase().includes(searchQuery.toLowerCase())
        ).map((conversation) => (
          <div
            key={conversation.id}
            onClick={() => setSelectedConversation(conversation)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '16px 20px',
              borderBottom: `1px solid ${colors.border}`,
              cursor: 'pointer',
              background: selectedConversation?.id === conversation.id 
                ? (isDark ? 'rgba(212, 175, 55, 0.1)' : 'rgba(139, 105, 20, 0.06)')
                : 'transparent',
              transition: 'background 0.2s ease'
            }}
          >
            <div style={{ position: 'relative' }}>
              <img
                src={conversation.photo}
                alt={conversation.name}
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: `2px solid ${colors.border}`
                }}
              />
              {conversation.unread > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: colors.gold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: isDark ? '#1A0F0A' : '#FFFFFF'
                }}>
                  {conversation.unread}
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <span style={{ color: colors.text, fontWeight: '600', fontSize: '15px' }}>
                  {conversation.name}
                </span>
                <span style={{ color: colors.textMuted, fontSize: '12px' }}>
                  {new Date(conversation.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p style={{
                color: colors.textMuted,
                fontSize: '14px',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {conversation.lastMessage}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Message Input (disabled if can't message) */}
      {messagingStatus.can_message && (
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            style={{
              flex: 1,
              padding: '14px 18px',
              background: isDark ? 'rgba(26, 15, 10, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${colors.border}`,
              borderRadius: '24px',
              color: colors.text,
              fontSize: '15px',
              outline: 'none'
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: newMessage.trim() ? colors.gold : colors.border,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease'
            }}
          >
            <Send size={20} color={isDark ? '#1A0F0A' : '#FFFFFF'} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
